import { useQuery } from '@tanstack/react-query'
import { ShieldCheck } from 'lucide-react'
import { aiApi } from '@/api/client'

interface Props {
  /**
   * Visual density:
   * - `card`: full glass-card with title + body. Used at the top of AI panes.
   * - `inline`: compact one-liner. Used inside collapsible source-intake sections.
   */
  variant?: 'card' | 'inline'
  className?: string
}

/**
 * The privacy notice surfaced anywhere source code or LLM inference is involved.
 * Hits {@code /api/ai/health} once so it can show the verbatim Ollama URL —
 * which is what makes the "nothing leaves your machine" claim auditable rather
 * than just a slogan: the user can see that inference is going to localhost.
 */
export default function PrivacyBanner({ variant = 'card', className }: Props) {
  const { data } = useQuery({
    queryKey: ['ai-health-banner'],
    queryFn: aiApi.health,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })
  const baseUrl = data?.baseUrl ?? 'http://localhost:11434'

  if (variant === 'inline') {
    return (
      <p
        className={`flex items-start gap-1.5 text-[11px] ${className ?? ''}`}
        style={{ color: 'var(--text-muted)' }}
      >
        <ShieldCheck size={12} className="shrink-0 mt-px text-accent" />
        <span>
          Source code never leaves this machine. Inference runs locally at{' '}
          <code className="font-mono">{baseUrl}</code>.
        </span>
      </p>
    )
  }

  return (
    <div
      className={`glass rounded-xl p-4 flex items-start gap-3 ${className ?? ''}`}
      style={{
        border: '1px solid rgba(99,102,241,0.25)',
        background: 'rgba(99,102,241,0.04)',
      }}
      role="note"
      aria-label="Privacy notice"
    >
      <div
        className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
        style={{ background: 'rgba(99,102,241,0.15)', color: 'var(--accent)' }}
      >
        <ShieldCheck size={16} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
          Local-only AI
        </p>
        <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
          Source code never leaves this machine. Suggestions are generated by{' '}
          <span className="font-medium" style={{ color: 'var(--text-primary)' }}>Ollama</span>{' '}
          running locally at{' '}
          <code className="font-mono px-1.5 py-0.5 rounded text-[11px]"
            style={{ background: 'rgba(0,0,0,0.3)', color: 'var(--text-primary)' }}>
            {baseUrl}
          </code>
          . Git PATs, when used, are held only for the clone and never persisted.
        </p>
      </div>
    </div>
  )
}
